import utils from '../../lib/utils'
import {Link} from 'react-router-dom'
import styles from './feed-post.module.css'
import Arrow from '../icons/arrow'
import PostTools from '../post-tools'
import {useBlock, useAuthorAddress, useEditedComment, useSubplebbit, useAuthorAvatar} from '@plebbit/plebbit-react-hooks'
import useUnreadReplyCount from '../../hooks/use-unread-reply-count'
import useUpvote from '../../hooks/use-upvote'
import useDownvote from '../../hooks/use-downvote'
import useCommentLabels from '../../hooks/use-comment-labels'
import {TipButton} from '../send-tip'
import {TipDisplay} from '../comment-tip'
import useBlacklist from '../../hooks/use-blacklist'
import useSetting from '../../hooks/use-setting'

const FeedPostMedia = ({mediaType, mediaUrl, link}) => {
  if (!mediaType) {
    return <div className={styles.noMedia}></div>
  }
  if (mediaType === 'image') {
    return (
      <div className={styles.mediaWrapper}>
        <Link to={link}>
          <img className={styles.media} src={mediaUrl} alt="" />
        </Link>
      </div>
    )
  }
  if (mediaType === 'video') {
    return (
      <div className={styles.mediaWrapper}>
        <Link to={link}>
          <video className={styles.media} controls={true} autoPlay={false} src={mediaUrl} />
        </Link>
      </div>
    )
  }
  if (mediaType === 'audio') {
    return (
      <Link to={link}>
        <audio controls={true} autoPlay={false} src={mediaUrl} />
      </Link>
    )
  }
  return <div className={styles.noMedia}></div>
}

const FeedPostAuthorAddress = ({post}) => {
  // show the public key author address for a few ms until the crypto name verification loads
  const {shortAuthorAddress, authorAddressChanged} = useAuthorAddress({comment: post})

  return (
    <Link className={styles.authorAddressWrapper} to={`/u/${post?.author?.address}/c/${post?.cid}`}>
      {/* use the crypto name as the width of the html element, but hide it and display the verified author address instead */}
      <span className={styles.authorAddressHidden}>{post?.author?.shortAddress}</span>
      {/* add css animation if the author address changed */}
      <span className={[styles.authorAddressVisible, authorAddressChanged ? styles.authorAddressChanged : undefined].join(' ')}>{shortAuthorAddress}</span>
    </Link>
  )
}

const FeedPostAuthorAvatar = ({post}) => {
  const {imageUrl} = useAuthorAvatar({author: post?.author})
  // if comment.author.avatar is defined, load empty space even without imageUrl
  // to not displace the feed after image loads
  if (!post?.author?.avatar) {
    return
  }
  return (
    <span className={styles.authorAvatarWrapper}>
      <img className={styles.authorAvatar} alt="" src={imageUrl} />
    </span>
  )
}

const FeedPost = ({post, updatedPost, index}) => {
  if (!updatedPost) {
    updatedPost = post
  }

  // handle pending mod or author edit
  const {state: editedPostState, editedComment: editedPost} = useEditedComment({comment: post})
  if (editedPost) {
    post = editedPost
  }

  let hostname
  try {
    hostname = new URL(post?.link).hostname.replace(/^www\./, '')
  } catch (e) {}

  const mediaType = utils.getCommentMediaType(post)

  let internalLink = `/p/${post?.subplebbitAddress}/c/${post?.cid}`
  // post is pending
  if (!post?.cid && post?.index !== undefined) {
    internalLink = `/profile/${post?.index}`
  }

  const {blocked: hidden} = useBlock({cid: post?.cid})

  const [unreadReplyCount] = useUnreadReplyCount(updatedPost)
  const unreadReplyCountText = typeof unreadReplyCount === 'number' ? `+${unreadReplyCount}` : ''

  const [upvoted, upvote] = useUpvote(post)
  const [downvoted, downvote] = useDownvote(post)

  let scoreNumber = updatedPost?.upvoteCount - updatedPost?.downvoteCount
  const negativeScoreNumber = scoreNumber < 0
  const largeScoreNumber = String(scoreNumber).length > 3
  if (isNaN(scoreNumber)) {
    scoreNumber = '-'
  }

  const labels = useCommentLabels(updatedPost, editedPostState)

  // Check for blacklist status
  const {isAuthorBlacklisted, isCommentBlacklisted} = useBlacklist()
  const [showBlacklistWarnings] = useSetting('showBlacklistWarnings', true)
  const [enableBlacklist] = useSetting('enableBlacklist', true)

  const authorBlacklisted = post?.author?.address && isAuthorBlacklisted(post.author.address)
  const commentBlacklisted = post?.cid && isCommentBlacklisted(post.cid)

  // Add blacklist labels if applicable and settings allow it
  const blacklistLabels = []
  if (enableBlacklist && showBlacklistWarnings) {
    if (commentBlacklisted) blacklistLabels.push('flagged')
    if (authorBlacklisted) blacklistLabels.push('author flagged')
  }
  const allLabels = [...labels, ...blacklistLabels]

  const title = (post?.title?.trim?.() || post?.content?.trim?.())?.substring?.(0, 300) || '-'

  // if sub address is not a domain, add sub title hint to address
  const subplebbitAddressIsDomain = post?.shortSubplebbitAddress && post?.shortSubplebbitAddress?.includes('.')
  const subplebbit = useSubplebbit({subplebbitAddress: !subplebbitAddressIsDomain ? post?.subplebbitAddress : undefined})
  const subplebbitAddress = subplebbit?.title
    ? `${post?.shortSubplebbitAddress.substring(0, 8)}-${subplebbit?.title?.replaceAll(' ', '').substring(0, 8).toLowerCase()}`
    : post?.shortSubplebbitAddress

  return (
    <div className={styles.feedPost}>
      <div className={styles.textWrapper}>
        <div className={styles.column}>
          <div className={styles.score}>
            <div onClick={upvote} className={[styles.upvote, upvoted ? styles.voteSelected : undefined].join(' ')}>
              <Arrow />
            </div>
            <PostTools post={post}>
              <div
                className={[
                  styles.scoreNumber,
                  largeScoreNumber ? styles.largeScoreNumber : undefined,
                  negativeScoreNumber ? styles.negativeScoreNumber : undefined,
                ].join(' ')}
              >
                {scoreNumber}
              </div>
            </PostTools>
            <div onClick={downvote} className={[styles.downvote, downvoted ? styles.voteSelected : undefined].join(' ')}>
              <Arrow />
            </div>
          </div>
        </div>
        <div className={[styles.column, hidden ? styles.hidden : undefined].join(' ')}>
          <div className={styles.header}>
            <Link to={internalLink} className={styles.title}>
              {title}
            </Link>
            {allLabels.map((label) => (
              <>
                {' '}
                <span
                  key={label}
                  className={[styles.label, label === 'flagged' || label === 'author flagged' ? styles.flaggedLabel : undefined].filter(Boolean).join(' ')}
                  style={{
                    ...(label === 'flagged' || label === 'author flagged'
                      ? {
                          background: '#ffebee',
                          color: '#c62828',
                          border: '1px solid #f44336',
                        }
                      : {}),
                  }}
                >
                  {label === 'flagged' ? '⚠️ flagged' : label === 'author flagged' ? '🚫 author flagged' : label}
                </span>
              </>
            ))}
            {hostname && (
              <Link to={post?.link} target="_blank" rel="noreferrer">
                {' '}
                {hostname}
              </Link>
            )}
          </div>
          <div className={styles.content}>
            <span className={styles.timestamp}>{utils.getFormattedTime(post?.timestamp)}</span>
            <span className={styles.author}>
              {' '}
              by <FeedPostAuthorAvatar post={post} />
              <FeedPostAuthorAddress post={post} /> to{' '}
            </span>
            <Link to={`/p/${post?.subplebbitAddress}`} className={styles.subplebbit}>
              {subplebbitAddress}
            </Link>
          </div>
          <div className={styles.footer}>
            <Link to={internalLink} className={[styles.button, styles.replyCount].join(' ')}>
              {updatedPost?.replyCount || 0} comments <span className={styles.unreadReplyCount}>{unreadReplyCountText}</span>
            </Link>
            <TipDisplay comment={post} subplebbit={subplebbit} />
            <TipButton comment={post} subplebbit={subplebbit} />
          </div>
        </div>
      </div>
      <div className={hidden || post?.removed ? styles.hidden : undefined}>
        <FeedPostMedia mediaType={mediaType} mediaUrl={post?.link} link={internalLink} />
      </div>
    </div>
  )
}

export default FeedPost
